import { safeLocalStorage } from '@/services/safeLocalStorage'

/**
 * API for storing boolean values in the browser's Local Storage.
 *
 * Helps manage various states of application. All writes route through
 * `safeLocalStorage` (the central guarded setter) so the invite-link token
 * tripwire can never be bypassed.
 */
function getFromStorage(storageKey: string): boolean {
  if (typeof window === 'undefined') return true

  const stored = safeLocalStorage.get(storageKey)
  if (stored) {
    return stored === 'true'
  }

  return false
}

function getNullableFromStorage(storageKey: string): boolean | null {
  if (typeof window === 'undefined') return null

  const stored = safeLocalStorage.get(storageKey)
  if (stored === null) return null
  return stored === 'true'
}

export const localStorageService = {
  set(storageKey: string, status: boolean): void {
    safeLocalStorage.set(storageKey, String(status))
  },

  get(storageKey: string): boolean {
    return getFromStorage(storageKey)
  },

  /**
   * Like `get`, but distinguishes "absent" from "explicitly false":
   * returns `null` when the key is missing (or storage is unavailable),
   * `true` for "true", `false` for any other stored value.
   *
   * Use this when the caller needs to apply different defaults for the
   * unset case (e.g. feature flags whose default depends on environment).
   */
  getNullable(storageKey: string): boolean | null {
    return getNullableFromStorage(storageKey)
  },

  clear(storageKey: string): void {
    safeLocalStorage.remove(storageKey)
  },
}
